<template>
  <v-container fluid class="tests-admin">
    <p class="text-center title mt-6">Тести</p>

    <v-row class="mt-5">
      <v-col cols="12" md="4" class="pt-0">
        <div class="d-flex justify-space-between align-center">
          <p class="text-sm-body-1 font-weight-bold mb-0">Усі тести</p>
          <v-btn small class="mb-2" @click="resetForm">
            Створити
            <v-icon color="green">mdi-plus</v-icon>
          </v-btn>
        </div>
        <p v-if="!tests.length && !listLoading" class="text-center mt-6 grey--text">Тестів ще немає</p>
        <v-card
          v-for="item in tests"
          :key="item.id"
          class="mt-2"
          :class="{ 'tests-admin__selected': item.id === testId }"
          @click="selectTest(item)"
        >
          <v-card-title class="text-sm-body-1 font-weight-bold">{{ item.title }}</v-card-title>
          <v-card-text class="d-flex align-center flex-wrap" style="gap: 12px">
            <v-icon small>{{ item.published ? "mdi-check-bold" : "mdi-progress-wrench" }}</v-icon>
            <span>{{ item.kind === "professional" ? "Професійний" : "Самопізнання" }}</span>
            <span>{{ item.questions_count }} питань</span>
            <span>пройшли {{ item.passed_count }}</span>
          </v-card-text>
        </v-card>
      </v-col>

      <v-col cols="12" md="8" class="pt-0">
        <p class="text-sm-body-1 font-weight-bold mb-3">{{ testId ? "Редагування тесту" : "Новий тест" }}</p>

        <v-card class="px-4 pt-2 pb-4">
          <v-text-field v-model="form.title" label="Назва" counter="255"></v-text-field>
          <v-radio-group v-model="form.kind" row hide-details class="mt-0">
            <v-radio value="professional" label="Професійний тест / опитувальник"></v-radio>
            <v-radio value="self" label="Тест для самопізнання"></v-radio>
          </v-radio-group>
          <p class="text-caption grey--text text--darken-1 mt-1 mb-2">
            Професійний психолог може рекомендувати клієнту в чаті; результат клієнта, що увійшов у кабінет, зберігається в «Мої тести»
            і приходить психологу. Результати тестів для самопізнання не зберігаються.
          </p>
          <v-select
            v-if="form.kind === 'professional'"
            v-model="form.symptoms"
            :items="symptomItems"
            item-text="title"
            item-value="key"
            label="Що турбує (фільтр «Що вас турбує» на сторінці «Тести»)"
            multiple
            small-chips
            deletable-chips
            class="mt-3"
          ></v-select>
          <v-textarea v-model="form.short_description" label="Короткий опис (для картки)" rows="2" auto-grow counter="1000"></v-textarea>
          <div class="d-flex flex-wrap" style="gap: 0 24px">
            <v-text-field v-model="form.minutes" type="number" min="1" label="Хвилин на проходження" style="flex: 1 1 180px"></v-text-field>
            <v-text-field v-model="form.sort" type="number" min="0" label="Порядок у списку (менше — вище)" style="flex: 1 1 180px"></v-text-field>
          </div>
          <v-text-field v-model="form.seo_title" label="SEO заголовок"></v-text-field>
          <v-textarea v-model="form.seo_description" label="SEO опис" rows="3" auto-grow></v-textarea>
        </v-card>

        <div v-show="!previewUrl" class="tests-admin__upload mt-4" @click="$refs.previewInput.click()">
          <div class="tests-admin__upload-inner"><span>Картинка тесту (необов'язково)</span></div>
        </div>
        <div v-show="previewUrl" class="tests-admin__preview mt-4" :style="{ backgroundImage: previewUrl ? `url(${previewUrl})` : '' }"></div>
        <input ref="previewInput" hidden type="file" accept="image/png,image/gif,image/jpeg,image/webp" @change="onPreviewPicked">
        <v-btn v-if="previewUrl" small text class="mt-1" @click="$refs.previewInput.click()">Замінити картинку</v-btn>

        <p class="text-sm-body-1 font-weight-bold mt-6 mb-2">Вступ перед тестом (інструкція)</p>
        <vue-editor id="test-editor" v-model="form.description" class="blog-editor" :editorToolbar="editorToolbar" />

        <!-- scales -->
        <v-card class="px-4 pt-4 pb-3 mt-6">
          <p class="subtitle-1 font-weight-bold mb-1">Шкали</p>
          <p class="text-caption grey--text text--darken-1 mb-2">
            Без шкал рахується одна загальна сума балів. Якщо тест має кілька шкал (напр. «Тривога», «Депресія»), додайте їх
            і вкажіть шкалу в кожному питанні — результат рахується по кожній окремо.
          </p>
          <div v-for="(scale, i) in form.scales" :key="'sc' + i" class="d-flex align-center" style="gap: 12px">
            <v-text-field v-model="scale.title" placeholder="Назва шкали" dense hide-details></v-text-field>
            <v-btn icon small color="error" @click="removeScale(i)"><v-icon>mdi-delete-outline</v-icon></v-btn>
          </div>
          <v-btn small text color="primary" class="mt-2" @click="addScale"><v-icon left small>mdi-plus</v-icon>Додати шкалу</v-btn>
        </v-card>

        <!-- questions -->
        <v-card class="px-4 pt-4 pb-3 mt-4">
          <div class="d-flex flex-wrap align-center" style="gap: 8px">
            <p class="subtitle-1 font-weight-bold mb-0">Питання ({{ form.questions.length }})</p>
            <v-spacer></v-spacer>
            <v-btn small depressed @click="bulkDialog = true"><v-icon left small>mdi-format-list-numbered</v-icon>Додати списком</v-btn>
          </div>
          <p class="text-caption grey--text text--darken-1 mt-1 mb-2">
            У кожного варіанта — бали. Для зворотних питань просто поставте бали у зворотному порядку.
          </p>
          <div v-for="(question, q) in form.questions" :key="question.key" class="tests-admin__question">
            <div class="d-flex align-start" style="gap: 8px">
              <span class="tests-admin__num">{{ q + 1 }}.</span>
              <v-textarea v-model="question.text" placeholder="Текст питання" rows="1" auto-grow dense hide-details></v-textarea>
              <v-btn icon small :disabled="q === 0" @click="moveQuestion(q, -1)"><v-icon>mdi-arrow-up</v-icon></v-btn>
              <v-btn icon small :disabled="q === form.questions.length - 1" @click="moveQuestion(q, 1)"><v-icon>mdi-arrow-down</v-icon></v-btn>
              <v-btn icon small color="error" @click="form.questions.splice(q, 1)"><v-icon>mdi-delete-outline</v-icon></v-btn>
            </div>
            <v-select
              v-if="form.scales.length"
              v-model="question.scale"
              :items="scaleItems"
              label="Шкала"
              dense
              hide-details
              class="tests-admin__scale"
            ></v-select>
            <div v-for="(option, o) in question.options" :key="o" class="d-flex align-center tests-admin__option" style="gap: 8px">
              <v-text-field v-model="option.text" placeholder="Варіант відповіді" dense hide-details></v-text-field>
              <v-text-field v-model="option.score" type="number" placeholder="Бали" dense hide-details class="tests-admin__score"></v-text-field>
              <v-btn icon x-small :disabled="question.options.length <= 2" @click="question.options.splice(o, 1)"><v-icon small>mdi-close</v-icon></v-btn>
            </div>
            <v-btn x-small text color="primary" class="mt-1" @click="question.options.push({ text: '', score: 0 })">+ варіант</v-btn>
          </div>
          <v-btn small text color="primary" class="mt-2" @click="addQuestion"><v-icon left small>mdi-plus</v-icon>Додати питання</v-btn>
        </v-card>

        <!-- results -->
        <v-card class="px-4 pt-4 pb-3 mt-4">
          <p class="subtitle-1 font-weight-bold mb-1">Результати</p>
          <p class="text-caption grey--text text--darken-1 mb-2">
            Діапазони балів і що вони означають. Максимум зараз: {{ maxText }}.
          </p>
          <div v-for="(item, r) in form.results" :key="'r' + r" class="tests-admin__result">
            <div class="d-flex flex-wrap align-center" style="gap: 8px">
              <v-select v-if="form.scales.length" v-model="item.scale" :items="scaleItems" label="Шкала" dense hide-details style="max-width: 200px"></v-select>
              <v-text-field v-model="item.min" type="number" label="Від" dense hide-details style="max-width: 90px"></v-text-field>
              <v-text-field v-model="item.max" type="number" label="До" dense hide-details style="max-width: 90px"></v-text-field>
              <v-text-field v-model="item.title" label="Назва результату" dense hide-details style="flex: 1 1 200px"></v-text-field>
              <v-btn icon small color="error" @click="form.results.splice(r, 1)"><v-icon>mdi-delete-outline</v-icon></v-btn>
            </div>
            <v-textarea v-model="item.text" placeholder="Пояснення результату" rows="1" auto-grow dense hide-details class="mt-2"></v-textarea>
          </div>
          <v-btn small text color="primary" class="mt-2" @click="addResult"><v-icon left small>mdi-plus</v-icon>Додати результат</v-btn>
        </v-card>

        <div class="d-flex flex-wrap align-center justify-space-between mt-4" style="gap: 16px">
          <v-checkbox v-model="form.published" color="primary" label="Опубліковано на сайті" class="mt-0 pt-0" hide-details></v-checkbox>
          <v-btn color="primary" depressed :loading="saving" @click="save">{{ testId ? "Зберегти" : "Створити тест" }}</v-btn>
        </div>
        <div v-if="testId" class="d-flex flex-wrap align-center justify-end mt-3">
          <v-btn v-if="form.published" small text color="primary" :href="siteUrl + '/tests/' + testSlug" target="_blank" rel="noopener">
            <v-icon left small>mdi-open-in-new</v-icon>Переглянути на сайті
          </v-btn>
          <v-btn small text color="error" @click="deleteDialog = true"><v-icon left small>mdi-delete-outline</v-icon>Видалити тест</v-btn>
        </div>
      </v-col>
    </v-row>

    <!-- many questions at once: one per line, the same answer options for all -->
    <v-dialog v-model="bulkDialog" max-width="640">
      <v-card class="pa-5">
        <p class="title mb-1">Додати питання списком</p>
        <p class="text-body-2 grey--text text--darken-1 mb-3">Кожне питання — з нового рядка. Усі отримають однакові варіанти відповідей нижче.</p>
        <v-textarea v-model="bulkQuestions" label="Питання" outlined rows="6"></v-textarea>
        <v-textarea
          v-model="bulkOptions"
          label="Варіанти: «текст = бали», кожен з нового рядка"
          outlined
          rows="4"
          hint="Напр.: Ніколи = 0"
          persistent-hint
        ></v-textarea>
        <div class="d-flex justify-end mt-3" style="gap: 8px">
          <v-btn text @click="bulkDialog = false">Скасувати</v-btn>
          <v-btn depressed color="primary" @click="addBulk">Додати</v-btn>
        </div>
      </v-card>
    </v-dialog>

    <v-dialog v-model="deleteDialog" max-width="420">
      <v-card>
        <v-card-text class="pt-6 text-body-1">Видалити цей тест разом з результатами клієнтів? Відновити буде неможливо.</v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="deleteDialog = false">Скасувати</v-btn>
          <v-btn color="error" depressed :loading="deleting" @click="deleteTest">Так, видалити</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import Vue from "vue";
import ApiClient from "@/libs/http-client";

let keySeed = 0;

function questionRow(item = {}) {
  return {
    key: ++keySeed,
    text: item.text || "",
    scale: item.scale || "",
    options: (item.options && item.options.length ? item.options : [{ text: "", score: 0 }, { text: "", score: 1 }]).map((option) => ({
      text: option.text,
      score: option.score,
    })),
  };
}

function emptyForm() {
  return {
    title: "",
    kind: "professional",
    short_description: "",
    description: "",
    seo_title: "",
    seo_description: "",
    minutes: null,
    sort: 0,
    published: false,
    scales: [],
    symptoms: [],
    questions: [questionRow()],
    results: [{ scale: "", min: 0, max: 0, title: "", text: "" }],
  };
}

function toast(text, icon = "success") {
  Vue.swal({
    target: document.getElementById("main"),
    text,
    icon,
    timer: 4000,
    toast: true,
    position: "top-right",
    showConfirmButton: false,
  });
}

export default {
  name: "TestsAdmin",
  metaInfo() {
    return { title: "Платформа / Тести" };
  },
  data() {
    return {
      tests: [],
      symptomItems: [],
      listLoading: false,
      form: emptyForm(),
      testId: null,
      testSlug: null,
      previewFile: null,
      previewUrl: null,
      saving: false,
      deleting: false,
      deleteDialog: false,
      bulkDialog: false,
      bulkQuestions: "",
      bulkOptions: "Ніколи = 0\nІноді = 1\nЧасто = 2\nМайже завжди = 3",
      siteUrl: (process.env.VUE_APP_MAIN_PAGE || "").replace(/\/$/, ""),
      editorToolbar: [
        [{ header: [2, 3, false] }],
        ["bold", "italic", "underline"],
        [{ list: "ordered" }, { list: "bullet" }],
        ["link"],
        ["clean"],
      ],
    };
  },
  computed: {
    // scales are keyed by their position: s1, s2...
    scaleItems() {
      return this.form.scales.map((scale, i) => ({ text: scale.title || `Шкала ${i + 1}`, value: scale.key }));
    },
    maxText() {
      const totals = {};
      this.form.questions.forEach((question) => {
        const key = this.form.scales.length ? question.scale || "" : "";
        const best = Math.max(...question.options.map((option) => Number(option.score) || 0));
        totals[key] = (totals[key] || 0) + best;
      });
      if (!this.form.scales.length) return String(totals[""] || 0);
      return this.form.scales.map((scale) => `${scale.title || scale.key} — ${totals[scale.key] || 0}`).join(", ");
    },
  },
  beforeMount() {
    this.loadTests();
  },
  methods: {
    loadTests() {
      this.listLoading = true;
      return ApiClient.get("api/admin/tests")
        .then((response) => {
          this.tests = response.data.data;
          this.symptomItems = response.data.symptoms || [];
        })
        .finally(() => {
          this.listLoading = false;
        });
    },
    resetForm() {
      this.form = emptyForm();
      this.testId = null;
      this.testSlug = null;
      this.previewFile = null;
      this.previewUrl = null;
    },
    selectTest(item) {
      this.form = {
        title: item.title || "",
        kind: item.kind,
        short_description: item.short_description || "",
        description: item.description || "",
        seo_title: item.seo_title || "",
        seo_description: item.seo_description || "",
        minutes: item.minutes,
        sort: item.sort || 0,
        published: !!item.published,
        scales: (item.scales || []).map((scale) => ({ ...scale })),
        symptoms: [...(item.symptoms || [])],
        questions: (item.questions || []).map(questionRow),
        results: (item.results || []).map((result) => ({ ...result })),
      };
      this.testId = item.id;
      this.testSlug = item.slug;
      this.previewFile = null;
      this.previewUrl = item.preview_image;
    },
    addScale() {
      let n = this.form.scales.length + 1;
      while (this.form.scales.some((scale) => scale.key === "s" + n)) n++;
      this.form.scales.push({ key: "s" + n, title: "" });
    },
    removeScale(i) {
      const key = this.form.scales[i].key;
      this.form.scales.splice(i, 1);
      this.form.questions.forEach((question) => {
        if (question.scale === key) question.scale = "";
      });
      this.form.results = this.form.results.filter((result) => result.scale !== key);
    },
    addQuestion() {
      const last = this.form.questions[this.form.questions.length - 1];
      // the options of the previous question are the usual start for the next one
      this.form.questions.push(questionRow({ scale: last ? last.scale : "", options: last ? last.options : null }));
    },
    moveQuestion(index, shift) {
      const list = this.form.questions.slice();
      const [item] = list.splice(index, 1);
      list.splice(index + shift, 0, item);
      this.form.questions = list;
    },
    addResult() {
      const last = this.form.results[this.form.results.length - 1];
      this.form.results.push({ scale: last ? last.scale : "", min: last ? Number(last.max) + 1 : 0, max: last ? Number(last.max) + 1 : 0, title: "", text: "" });
    },
    addBulk() {
      const options = this.bulkOptions.split("\n").map((line) => line.trim()).filter(Boolean).map((line) => {
        const match = line.match(/^(.*?)\s*=\s*(-?\d+(?:[.,]\d+)?)\s*$/);
        return match ? { text: match[1], score: Number(match[2].replace(",", ".")) } : { text: line, score: 0 };
      });
      if (options.length < 2) {
        toast("Потрібно щонайменше 2 варіанти відповіді", "warning");
        return;
      }
      const questions = this.bulkQuestions.split("\n").map((line) => line.replace(/^\s*\d+[.)]\s*/, "").trim()).filter(Boolean);
      // an empty first question of a new test is replaced
      if (this.form.questions.length === 1 && !this.form.questions[0].text.trim()) this.form.questions = [];
      questions.forEach((text) => this.form.questions.push(questionRow({ text, options })));
      this.bulkQuestions = "";
      this.bulkDialog = false;
    },
    onPreviewPicked(event) {
      const file = event.target.files[0];
      if (!file) return;
      this.previewFile = file;
      this.previewUrl = URL.createObjectURL(file);
      event.target.value = "";
    },
    save() {
      const payload = new FormData();
      ["title", "kind", "short_description", "description", "seo_title", "seo_description"].forEach((field) => {
        payload.append(field, this.form[field] == null ? "" : this.form[field]);
      });
      if (this.form.minutes) payload.append("minutes", this.form.minutes);
      payload.append("sort", this.form.sort || 0);
      payload.append("published", this.form.published ? 1 : 0);
      payload.append("symptoms", JSON.stringify(this.form.kind === "professional" ? this.form.symptoms : []));
      payload.append("scales", JSON.stringify(this.form.scales.map((scale) => ({ key: scale.key, title: scale.title }))));
      payload.append("questions", JSON.stringify(this.form.questions.map((question) => ({
        text: question.text,
        scale: this.form.scales.length ? question.scale || "" : "",
        options: question.options.map((option) => ({ text: option.text, score: Number(option.score) || 0 })),
      }))));
      payload.append("results", JSON.stringify(this.form.results.map((result) => ({
        scale: this.form.scales.length ? result.scale || "" : "",
        min: Number(result.min) || 0,
        max: Number(result.max) || 0,
        title: result.title,
        text: result.text,
      }))));
      if (this.previewFile) payload.append("preview_image", this.previewFile);

      this.saving = true;
      ApiClient.post(this.testId ? `api/admin/tests/${this.testId}` : "api/admin/tests", payload, {
        headers: { "Content-Type": "multipart/form-data" },
      })
        .then((response) => {
          toast(response.data.message);
          this.selectTest(response.data.data);
          this.loadTests();
        })
        .finally(() => {
          this.saving = false;
        });
    },
    deleteTest() {
      this.deleting = true;
      ApiClient.delete(`api/admin/tests/${this.testId}`)
        .then((response) => {
          toast(response.data.message);
          this.deleteDialog = false;
          this.resetForm();
          this.loadTests();
        })
        .finally(() => {
          this.deleting = false;
        });
    },
  },
};
</script>

<style lang="scss" scoped>
.tests-admin__selected {
  outline: 2px solid $primary;
}
.tests-admin__question {
  padding: 12px 0 10px;
  border-bottom: 1px solid #e3e6e8;
}
.tests-admin__num {
  padding-top: 6px;
  min-width: 26px;
  color: #868686;
}
.tests-admin__scale {
  max-width: 260px;
  margin: 8px 0 0 34px;
}
.tests-admin__option {
  margin: 6px 0 0 34px;
}
.tests-admin__score {
  flex: 0 0 90px;
  max-width: 90px;
}
.tests-admin__result {
  padding: 10px 0 12px;
  border-bottom: 1px solid #e3e6e8;
}
.tests-admin__upload {
  display: flex;
  width: 450px;
  max-width: 100%;
  height: 160px;
  padding: 10px;
  cursor: pointer;
  &:hover {
    background-color: rgba(0, 0, 0, 0.05);
  }
}
.tests-admin__upload-inner {
  display: flex;
  flex: 1;
  align-items: center;
  justify-content: center;
  border: 3px dashed $primary;
  font-size: 18px;
}
.tests-admin__preview {
  width: 450px;
  max-width: 100%;
  height: 250px;
  background-size: cover;
  background-position: center;
  border-radius: 12px;
}
</style>
